import React, { useMemo, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw } from "lucide-react";
import { useCurrentDomain } from "../../context/DomainContext";
import { useCompanies, useDoctors, useTemplates } from "../../hooks";
import { getMedicalNoteTemplateUsage } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import CompanyUsageModal from "./CompanyUsageModal";
import DoctorUsageModal from "./DoctorUsageModal";
import UsageDetailModal from "./UsageDetailModal";
import "./MedicalNoteTemplateUsageReport.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const MedicalNoteTemplateUsageReport = () => {
  const { domain } = useCurrentDomain();

  const templates = useTemplates(domain);
  const companies = useCompanies(domain);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const doctors = useDoctors(selectedCompany?.value || "", domain);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [companiesByTemplate, setCompaniesByTemplate] = useState({});
  const [doctorsByCompany, setDoctorsByCompany] = useState({});

  const [companyModal, setCompanyModal] = useState(null);
  const [doctorModal, setDoctorModal] = useState(null);
  const [usageModal, setUsageModal] = useState(null);

  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const templateOptions = useMemo(() => {
    const list = Array.isArray(templates) ? templates : [];
    return list.map((t) => ({
      value: String(t.value),
      label: t.label,
    }));
  }, [templates]);

  const companyOptions = useMemo(() => {
    const list = Array.isArray(companies) ? companies : companies?.data || companies?.companies || [];
    return list.map((c) => ({
      value: c.szCompanyId || c.companyId || c.value || c.SZ_COMPANY_ID,
      label: `${c.szCompanyName || c.companyName || c.label || c.SZ_COMPANY_NAME || ""} (${c.szCompanyId || c.companyId || c.SZ_COMPANY_ID || ""})`,
    }));
  }, [companies]);

  const doctorOptions = useMemo(() => {
    const raw = doctors?.doctors ?? doctors?.data ?? doctors;
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return list.map((d) => {
      const id = d.szDoctorId || d.doctorId || d.SZ_DOCTOR_ID || d.value;
      const name = d.szDoctorName || d.doctorName || d.SZ_DOCTOR_NAME || d.label || "";
      return { value: id, label: `${name} (${id})` };
    });
  }, [doctors]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const groupResponse = (data) => {
    const companiesMap = {};
    (data.companies || []).forEach((c) => {
      if (!companiesMap[c.templateId]) companiesMap[c.templateId] = [];
      companiesMap[c.templateId].push(c);
    });
    const doctorsMap = {};
    (data.doctors || []).forEach((d) => {
      const key = `${d.templateId}|${d.companyId}`;
      if (!doctorsMap[key]) doctorsMap[key] = [];
      doctorsMap[key].push(d);
    });
    setRows(data.templates || []);
    setCompaniesByTemplate(companiesMap);
    setDoctorsByCompany(doctorsMap);
    setTotalCount(data.totalCount || 0);
  };

  const loadPage = async (page, size) => {
    if (!domain) {
      toast.error("Domain is missing");
      return;
    }
    setLoading(true);
    try {
      const data = await getMedicalNoteTemplateUsage({
        domain,
        templateIds: selectedTemplates.length ? selectedTemplates.map((t) => t.value).join(",") : "",
        companyId: selectedCompany?.value || "",
        doctorId: selectedDoctor?.value || "",
        pageNumber: page,
        pageSize: size,
      });
      groupResponse(data || {});
      setPageNumber(page);
      setPageSize(size);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load report");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedTemplates([]);
    setSelectedCompany(null);
    setSelectedDoctor(null);
    setRows([]);
    setCompaniesByTemplate({});
    setDoctorsByCompany({});
    setCompanyModal(null);
    setDoctorModal(null);
    setUsageModal(null);
    setTotalCount(0);
    setPageNumber(1);
    setPageSize(25);
  };

  return (
    <div className="mntu-page">
      <Toaster position="top-right" />

      <div className="mntu-filters">
        <div className="mntu-field">
          <label>Template</label>
          <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
            options={[...templateOptions].sort((a, b) =>
              String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: "base" })
            )}
            value={selectedTemplates}
            onChange={(v) => setSelectedTemplates(v || [])}
            styles={{
              ...selectStyles,
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base, state) => ({
                ...(selectStyles.control ? selectStyles.control(base, state) : base),
                minHeight: 32,
                height: "auto",
              }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                paddingTop: 2,
                paddingBottom: 2,
              }),
              multiValue: (base) => ({
                ...base,
                background: "#e6f7f1",
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: "#0aa375",
                fontSize: 12,
              }),
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            controlShouldRenderValue={true}
            placeholder="Select template(s)"
            isClearable
            filterOption={(option, input) => {
              const q = (input || "").trim().toLowerCase();
              if (!q) return true;
              const label = String(option.label || "").toLowerCase().replace(/[._]/g, " ");
              return q.split(/\s+/).every((part) => label.includes(part.replace(/[._]/g, " ")));
            }}
            formatOptionLabel={(option, { context }) =>
              context === "menu" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selectedTemplates.some((t) => String(t.value) === String(option.value))}
                    readOnly
                  />
                  <span>{option.label}</span>
                </div>
              ) : (
                option.label
              )
            }
          />
        </div>
        <div className="mntu-field">
          <label>Company</label>
          <Select
            options={companyOptions}
            value={selectedCompany}
            onChange={(v) => {
              setSelectedCompany(v);
              setSelectedDoctor(null);
            }}
            styles={selectStyles}
            placeholder="Select company"
            isClearable
          />
        </div>
        <div className="mntu-field">
          <label>Doctor</label>
          <Select
            options={doctorOptions}
            value={selectedDoctor}
            onChange={setSelectedDoctor}
            styles={selectStyles}
            placeholder={selectedCompany ? "Select doctor" : "Select company first"}
            isDisabled={!selectedCompany}
            isClearable
          />
        </div>
        <div className="mntu-actions">
          <button type="button" className="mntu-btn mntu-btn-search" onClick={() => loadPage(1, pageSize)} disabled={loading}>
            <Search size={16} />
            {loading ? "Loading..." : "Search"}
          </button>
          <button type="button" className="mntu-btn mntu-btn-reset" onClick={handleReset} disabled={loading}>
            <RefreshCw size={16} />
            Reset
          </button>
        </div>
      </div>

      <div className="mntu-table-wrap">
        <table className="mntu-table">
          <thead>
            <tr>
              <th>Sl No</th>
              <th>Template Name</th>
              <th>Template Id</th>
              <th>Account Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="mntu-empty">
                  {loading ? "Loading..." : "No results"}
                </td>
              </tr>
            ) : (
              rows.map((t, index) => (
                <tr key={t.templateId}>
                  <td>{(safePage - 1) * pageSize + index + 1}</td>
                  <td>
                    <button
                      type="button"
                      className="mntu-link"
                      onClick={() =>
                        setCompanyModal({
                          templateId: t.templateId,
                          templateName: t.templateName,
                          rows: companiesByTemplate[t.templateId] || [],
                        })
                      }
                    >
                      {t.templateName}
                    </button>
                  </td>
                  <td>{t.templateId}</td>
                  <td>
                     <button
                      type="button"
                      className="mntu-link"
                      onClick={() =>
                        setCompanyModal({
                          templateId: t.templateId,
                          templateName: t.templateName,
                          rows: companiesByTemplate[t.templateId] || [],
                        })
                      }
                    >
                      {t.companyCount}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>        
      </div>

      {totalCount > 0 && (
          <div className="mntu-pager">
            <Select
              options={pageSizeOptions}
              value={pageSizeOptions.find((o) => o.value === pageSize)}
              onChange={(opt) => loadPage(1, opt.value)}
              styles={selectStyles}
              menuPlacement="top"
              isSearchable={false}
            />
            <span>
              {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
            </span>
            <button type="button" className="mntu-btn mntu-btn-reset" disabled={safePage <= 1} onClick={() => loadPage(safePage - 1, pageSize)}>
              Prev
            </button>
            <button type="button" className="mntu-btn mntu-btn-reset" disabled={safePage >= totalPages} onClick={() => loadPage(safePage + 1, pageSize)}>
              Next
            </button>
          </div>
        )}

      {companyModal && (
        <CompanyUsageModal
          templateName={companyModal.templateName}
          rows={companyModal.rows}
          onClose={() => setCompanyModal(null)}
          onCompanyClick={(company) =>
            setDoctorModal({
              templateId: company.templateId,
              companyName: company.companyName,
              companyId: company.companyId,
              rows: doctorsByCompany[`${company.templateId}|${company.companyId}`] || [],
            })
          }
        />
      )}

      {doctorModal && (
        <DoctorUsageModal
          companyName={doctorModal.companyName}
          companyId={doctorModal.companyId}
          rows={doctorModal.rows}
          onClose={() => setDoctorModal(null)}
          onDoctorClick={(d) =>
            setUsageModal({
              templateId: doctorModal.templateId,
              companyId: d.companyId,
              doctorId: d.doctorId,
              doctorName: d.doctorName,
            })
          }
        />
      )}

      {usageModal && (
        <UsageDetailModal
          templateId={usageModal.templateId}
          companyId={usageModal.companyId}
          doctorId={usageModal.doctorId}
          doctorName={usageModal.doctorName}
          onClose={() => setUsageModal(null)}
        />
      )}
    </div>
  );
};

export default MedicalNoteTemplateUsageReport;